import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Clock, Calendar, Phone, MessageSquare, ChevronLeft } from "lucide-react";
import { getArticle, resolveImage } from "@/lib/blogApi";
import SEO from "@/components/SEO";
import { buildGraph, breadcrumbJsonLd, faqJsonLd } from "@/data/seo";

const RED = "#D41F30";
const SITE = "https://www.cityworthloans.com";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function articleJsonLd(a) {
  return {
    "@type": "BlogPosting",
    "@id": `${SITE}/blog/${a.slug}#article`,
    headline: a.title,
    description: a.meta_description,
    image: a.image_url ? resolveImage(a.image_url) : undefined,
    datePublished: a.published_at,
    dateModified: a.updated_at || a.published_at,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${a.slug}` },
    author: {
      "@type": "Organization",
      name: a.author?.name || "CityWorth Mortgage",
      url: SITE,
    },
    publisher: {
      "@type": "Organization",
      name: "CityWorth Mortgage",
      url: SITE,
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_happy-homes-ahead/artifacts/0buyd3la_CW%20Logo%20%281%29.svg",
      },
    },
    keywords: [a.primary_keyword, ...(a.secondary_keywords || [])].filter(Boolean).join(", "),
  };
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setArticle(null);
    getArticle(slug)
      .then((d) => !cancelled && setArticle(d))
      .catch((e) => {
        if (cancelled) return;
        if (e.status === 404) setNotFound(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !article) {
    return (
      <div className="min-h-screen pt-32 pb-24 text-center text-gray-500" data-testid="blog-post-loading">
        Loading article...
      </div>
    );
  }

  const ldGraph = buildGraph(
    articleJsonLd(article),
    article.faqs && article.faqs.length ? faqJsonLd(article.faqs.map((f) => ({ q: f.question, a: f.answer }))) : null,
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: article.title, path: `/blog/${article.slug}` },
    ])
  );

  return (
    <div className="bg-white min-h-screen" data-testid="blog-post-page">
      <SEO
        title={article.seo_title || article.title}
        description={article.meta_description}
        path={`/blog/${article.slug}`}
        ogImage={article.image_url ? resolveImage(article.image_url) : undefined}
        type="article"
        jsonLd={ldGraph}
      />

      <article>
        {/* Header */}
        <header className="pt-28 pb-8 md:pt-32 md:pb-12 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-[#D41F30] transition-colors mb-6"
              data-testid="blog-post-back"
            >
              <ChevronLeft className="w-4 h-4" /> All articles
            </Link>
            <div className="flex items-center gap-3 text-xs font-bold tracking-wider uppercase mb-5" style={{ color: RED }}>
              <span>{article.category}</span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {article.reading_time} min read
              </span>
              {article.published_at && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {formatDate(article.published_at)}
                  </span>
                </>
              )}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight mb-6">
              {article.title}
            </h1>
            {article.meta_description && (
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                {article.meta_description}
              </p>
            )}
          </div>
        </header>

        {/* Hero image */}
        {article.image_url && (
          <div className="max-w-5xl mx-auto px-6 mb-12">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src={resolveImage(article.image_url)}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <div
            className="prose prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-headings:text-gray-900 prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:font-black prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-[#D41F30] prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-[#D41F30] prose-blockquote:bg-red-50 prose-blockquote:rounded-r-2xl prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:text-gray-800 prose-blockquote:font-medium"
            data-testid="blog-post-body"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_md || ""}</ReactMarkdown>
          </div>

          {/* Author */}
          {article.author && (
            <div className="mt-12 p-6 bg-gray-50 rounded-2xl flex items-start gap-4" data-testid="blog-post-author">
              <div className="w-14 h-14 rounded-full bg-[#D41F30] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-black text-lg">CW</span>
              </div>
              <div>
                <p className="font-display text-lg font-bold text-gray-900">{article.author.name}</p>
                <p className="text-sm text-gray-500 mb-2">{article.author.title}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{article.author.bio}</p>
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
            Ready to start your homebuying journey?
          </h2>
          <p className="text-base md:text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            Talk to a CityWorth advisor for free, personalized guidance on your next step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/get-started"
              data-testid="blog-post-cta-start"
              className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white border-2 border-[#D41F30] hover:border-[#B91C2A] rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Start my homebuying plan <ArrowRight className="ml-2 w-4 h-4" />
            </a>
            <a
              href="tel:+15715208688"
              data-testid="blog-post-cta-call"
              className="inline-flex items-center justify-center bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-6 py-3.5 text-base font-semibold transition-all duration-300"
            >
              <Phone className="mr-2 w-4 h-4" /> 571-520-8688
            </a>
          </div>
        </div>
      </section>

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-black text-gray-900 mb-8 leading-tight">
              Related articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="blog-post-related">
              {article.related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300"
                >
                  {r.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={resolveImage(r.image_url)}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-xs font-bold tracking-wider uppercase mb-2" style={{ color: RED }}>
                      {r.category}
                    </div>
                    <h3 className="font-display text-lg font-bold text-gray-900 leading-tight group-hover:text-[#D41F30] transition-colors">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
